// ── DOM helpers ───────────────────────────────────────────────────────────────

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  html?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

/** HTML-escape a string for safe insertion into innerHTML */
export function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Extract a displayable size value from the backend's size_recommendation field. */
export function normalizeSizeValue(value: unknown): string | undefined {  if (typeof value === 'string' && value.trim() !== '') return value;
  if (typeof value === 'number') return String(value);
  if (value && typeof value === 'object') {
    const candidate = value as Record<string, unknown>;
    if (typeof candidate.size === 'string' && candidate.size.trim() !== '') return candidate.size;
    if (typeof candidate.letter === 'string' && candidate.letter.trim() !== '') return candidate.letter;
    if (typeof candidate.numeric === 'number') return String(candidate.numeric);
    if (typeof candidate.letter_size === 'string' && candidate.letter_size.trim() !== '') return candidate.letter_size;
    if (typeof candidate.numeric_size === 'number') return String(candidate.numeric_size);
  }
  return undefined;
}

/** Normalize a size_recommendation payload into the shape used by the UI. */
export function parseSizeRec(
  sr: { upper?: unknown; lower?: unknown } | null | undefined,
): { upper?: string; lower?: string } | null {
  const upper = normalizeSizeValue(sr?.upper);
  const lower = normalizeSizeValue(sr?.lower);
  if (upper === undefined && lower === undefined) return null;
  return { upper, lower };
}

/** Nouns that are plural-only (or uncountable) in fashion contexts — never singularized. */
const PLURAL_ONLY = new Set([
  'jeans', 'pants', 'trousers', 'shorts', 'leggings', 'joggers', 'tights',
  'slacks', 'chinos', 'overalls', 'dungarees', 'sweatpants', 'sweats',
  'sunglasses', 'glasses', 'briefs', 'boxers', 'knickers', 'underpants',
  'pajamas', 'pyjamas', 'scissors', 'tweezers', 'binoculars',
]);

/** Singular forms that the generic rules would get wrong (e.g. -ies -> -y yields "Hoody"). */
const IRREGULAR_SINGULAR: Record<string, string> = {
  hoodies: 'hoodie',
  denims: 'denim',
};

function singularizeWord(word: string): string {
  const key = word.toLowerCase();
  if (PLURAL_ONLY.has(key)) return word;
  if (IRREGULAR_SINGULAR[key]) return IRREGULAR_SINGULAR[key];
  if (/([^aeiou])ies$/i.test(word)) return word.slice(0, -3) + 'y';
  if (/(ses|xes|zes|ches|shes)$/i.test(word)) return word.slice(0, -2);
  if (/s$/i.test(word) && !/ss$/i.test(word) && !/(us|is|os)$/i.test(word)) return word.slice(0, -1);
  return word;
}

/**
 * Best-effort English singularization for category labels (e.g. "Tops" -> "Top",
 * "Maxi Dresses" -> "Maxi Dress"). Only the last word of a phrase is touched;
 * plural-only nouns like "Jeans" are left as-is.
 */
export function singularize(phrase: string): string {
  const parts = phrase.trim().split(/\s+/);
  if (parts.length === 0) return phrase;
  parts[parts.length - 1] = singularizeWord(parts[parts.length - 1]);
  return parts.join(' ');
}

// ── Canonical garment categories ────────────────────────────────────────────
// Mirrors the backend's normalizeCategory util so the SDK can resolve a
// product's try-on type (top/bottom/outerwear/dress/...) client-side.

const CANONICAL_MAP: Record<string, string> = {
  hat: 'hat', hats: 'hat', cap: 'hat', beanie: 'hat',
  top: 'top', tops: 'top', shirt: 'top', 't-shirt': 'top', tshirt: 'top', blouse: 'top',
  sweater: 'top', hoodie: 'top', polo: 'top', tank: 'top', camisole: 'top', cardigan: 'top',
  outerwear: 'outerwear', jacket: 'outerwear', blazer: 'outerwear', coat: 'outerwear',
  overcoat: 'outerwear', bomber: 'outerwear', parka: 'outerwear', vest: 'outerwear', gilet: 'outerwear',
  dress: 'dress', dresses: 'dress', gown: 'dress', jumpsuit: 'dress', romper: 'dress',
  bottom: 'bottom', bottoms: 'bottom', pant: 'bottom', pants: 'bottom', jeans: 'bottom', trouser: 'bottom',
  trousers: 'bottom', skirt: 'bottom', shorts: 'bottom', legging: 'bottom', leggings: 'bottom',
  jogger: 'bottom', chinos: 'bottom',
  shoe: 'shoe', shoes: 'shoe', sneaker: 'shoe', sneakers: 'shoe', boot: 'shoe',
  boots: 'shoe', sandal: 'shoe', loafer: 'shoe',
};

const CANONICAL_CATEGORIES = Object.values(CANONICAL_MAP).filter(
  (v, i, a) => a.indexOf(v) === i,
);

/** Map a raw category string to its canonical garment type, or null if unknown. */
export function toCanonicalCategory(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = raw.toLowerCase().trim();
  if (CANONICAL_CATEGORIES.includes(v)) return v;
  for (const [keyword, canonical] of Object.entries(CANONICAL_MAP)) {
    if (v.includes(keyword)) return canonical;
  }
  return null;
}

/**
 * Exact-token try-on type match. A tag token only maps to a canonical type when
 * it matches a known keyword as a WHOLE token (e.g. "Cap" -> hat, "Boots" ->
 * shoe). Substring matches ("Cap Sleeve" -> hat, "Bootleg" -> shoe) are
 * rejected — those are garment styles, not try-on types.
 */
function exactTryonType(token: string): string | null {
  return CANONICAL_MAP[token.toLowerCase().trim()] ?? null;
}

/**
 * Derive a product's canonical try-on type from its raw tags (e.g. "Top",
 * "Outerwear", the combined "Top, Outerwear", "Tops").
 *
 * Special rule: a product tagged with BOTH top and outerwear is classified as
 * outerwear internally (drives combo matching); "top" is only the model-facing
 * try-on type for single try-ons.
 */
export function tryonTypeFromTags(
  tags: (string | null | undefined)[] | null | undefined,
): string | null {
  if (!Array.isArray(tags)) return null;

  const types = new Set<string>();
  for (const tag of tags) {
    if (!tag) continue;
    for (const part of String(tag).split(',')) {
      const c = exactTryonType(part);
      if (c) types.add(c);
    }
  }

  if (types.has('top') && types.has('outerwear')) return 'outerwear';

  const PRIORITY = ['hat', 'shoe', 'dress', 'bottom', 'outerwear', 'top'];
  for (const t of PRIORITY) {
    if (types.has(t)) return t;
  }
  return null;
}
